import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, InputAdornment, MenuItem, Pagination, Stack, TextField,
} from '@mui/material';
import {
  Search as SearchIcon, Download as DownloadIcon, Add as AddIcon, FolderOutlined as FolderIcon,
} from '@mui/icons-material';
import DocumentService, {
  CategoryCount, DocumentCategory, DocumentFolder, DocumentStatus, TopOpsDocument,
} from '../../api/services/document.service';
import { categoryLabel } from '../../components/files/vaultUI';
import FilesShell, { FileDocumentButton, useFileUpload } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import FileTable from '../../components/files/FileTable';
import DocumentInspector from '../../components/files/DocumentInspector';
import { downloadBlob } from '../../utils/download';

// Two screens in one, because they are the same list with a different
// starting filter: a category binder, and global search.

const PAGE_SIZE = 25;

/** Files straight into the open folder, its type already chosen. */
const AddToFolderButton: React.FC<{ typeKey: string }> = ({ typeKey }) => {
  const openUpload = useFileUpload();
  return (
    <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => openUpload({ typeKey })}>
      Add file
    </Button>
  );
};

const DocumentListPage: React.FC<{ mode: 'category' | 'search' }> = ({ mode }) => {
  const navigate = useNavigate();
  const { category } = useParams<{ category: string }>();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState(params.get('q') || '');
  const [status, setStatus] = useState<DocumentStatus | ''>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<TopOpsDocument[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  /** The category's folders; ?folder= opens one. */
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const folder = mode === 'category' ? params.get('folder') || '' : '';
  const [selected, setSelected] = useState<TopOpsDocument | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const query = params.get('q') || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await DocumentService.list({
        category: mode === 'category' ? (category as DocumentCategory) : undefined,
        search: mode === 'search' ? query : undefined,
        status: status || undefined,
        documentTypeKey: folder || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items || []);
      setTotal(result.total || 0);
      setFolders(result.folders || []);
      setError(null);
      // The rail needs the category list on every Files screen; a failure
      // there costs a rail entry, not the page.
      DocumentService.getCategories().then(setCategories).catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load these files');
    } finally {
      setLoading(false);
    }
  }, [mode, category, query, status, folder, page]);

  useEffect(() => { load(); }, [load]);
  // A new search term is a new list, not page 7 of the old one.
  useEffect(() => { setPage(1); }, [query, status, category, folder]);
  const openFolder = (key: string) => setParams(key ? { folder: key } : {});
  const openFolderLabel = folders.find((f) => f.documentTypeKey === folder)?.label;

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setParams(search.trim() ? { q: search.trim() } : {});
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await DocumentService.exportZip({ scope: 'company', category: category || 'all' });
      downloadBlob(blob, `${category || 'documents'}-export.zip`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build that export');
    } finally {
      setExporting(false);
    }
  };

  const title = mode === 'category' ? (openFolderLabel || categoryLabel(category || '')) : 'Search';
  usePageBreadcrumb(openFolderLabel
    ? [{ label: categoryLabel(category || ''), to: `/files/category/${category}` }, { label: openFolderLabel }]
    : [{ label: title }]);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <FilesShell
      categories={categories}
      onUploaded={load}
      inspector={<DocumentInspector document={selected} />}
    
      header={
        <PageHeader
          title={title}
          subtitle={`${total} ${total === 1 ? 'file' : 'files'}${mode === 'search' && query ? ` matching “${query}”` : ''}`}
          actions={
            <>
              {mode === 'category' && (
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>
                  {exporting ? 'Preparing…' : 'Export'}
                </Button>
              )}
              {folder ? <AddToFolderButton typeKey={folder} /> : <FileDocumentButton />}
            </>
          }
        />
      }
    >
      {/* The binder's folders. A folder keeps every file filed into it —
          every week's statement, every invoice — so it is opened, not
          replaced. */}
      {folders.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
          <Chip label="All" variant={folder ? 'outlined' : 'filled'} color={folder ? 'default' : 'primary'} onClick={() => openFolder('')} />
          {folders.map((f) => (
            <Chip
              key={f.documentTypeKey}
              icon={<FolderIcon />}
              label={`${f.label} (${f.count})`}
              variant={folder === f.documentTypeKey ? 'filled' : 'outlined'}
              color={folder === f.documentTypeKey ? 'primary' : 'default'}
              onClick={() => openFolder(f.documentTypeKey)}
            />
          ))}
        </Stack>
      )}
      {/* The in-page breadcrumb is gone: it said the same thing the
          topbar now says, one line lower and in a different type size. */}

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ my: 2 }}>
        <Box component="form" onSubmit={submitSearch} sx={{ flex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, type or owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </Box>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as DocumentStatus | '')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Any status</MenuItem>
          <MenuItem value="current">Current</MenuItem>
          <MenuItem value="expiring_soon">Expiring soon</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
          <MenuItem value="archived">Superseded</MenuItem>
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <LoadingState label="Loading files…" />
      ) : (
        <>
          <FileTable
            documents={items}
            showCategory={mode === 'search'}
            selectedId={selected?._id}
            onSelect={setSelected}
            onOpen={(document) => navigate(`/files/document/${document._id}`)}
            emptyMessage={
              mode === 'search' && !query
                ? 'Type something to search the vault.'
                : mode === 'category' && category === 'payroll'
                  ? 'Each week’s statement and weekly service land here when you upload them in Payroll.'
                  : 'Nothing here.'
            }
          />

          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} />
            </Stack>
          )}
        </>
      )}
    </FilesShell>
  );
};

export default DocumentListPage;
